import {
  RenderFormat,
  ViewerDocumentKind,
  ViewerDownloadTargetKind,
  ViewerOpenContextKind,
  type ContentViewerSession,
  type ViewerUserFileRenderAsset,
} from '@/content-viewer/store/schemas'
import { createViewerSession } from '@/content-viewer/utils/create-viewer-session'
import { MimeType } from '@/constants/constants-mime-types'
import type { UserFileRead, UserFileViewableContent } from '@/uppy-file-upload/schemas'
import { AgentReferenceType, type ClientAgentReference } from '../store/schemas'
import { agentReferenceMetadataToTitle } from './agent-reference-title'
import { FileReferenceTargetKind, resolveFileReferenceTarget } from './file-reference-target'
import { resolveReferenceStorage } from '@/chat-common/reference-storage/resolve-reference-storage'
import type { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { buildChatReferenceFromAgentReference } from '@/analytics/utils/ria-source-analytics'
import { getHighlightsForCitation } from './get-highlights-for-citation'

type BuildAgentReferenceViewerSessionArgs = {
  reference: ClientAgentReference
  matterId?: string | null
  citationHighlightIndex?: number
  defaultTab?: CaseLawReferenceTab
  userFile?: UserFileRead | null
}

function buildChatCitationContext(reference: ClientAgentReference, citationHighlightIndex?: number) {
  return {
    kind: ViewerOpenContextKind.CHAT_CITATION,
    agentReferenceId: reference.reference.id,
    citationId: reference.reference.citation_id,
    turnId: reference.reference.turn_id,
    conversationId: reference.conversation_id,
    analyticsReference: buildChatReferenceFromAgentReference(reference),
    ...(citationHighlightIndex !== undefined ? { highlightIndex: citationHighlightIndex } : {}),
  } as const
}

function buildUserFileReferenceRenderAsset(viewableContent: UserFileViewableContent): ViewerUserFileRenderAsset | null {
  if (viewableContent.content_type === MimeType.PDF) {
    return {
      format: RenderFormat.PDF,
    }
  }

  if (viewableContent.content_type === MimeType.MARKDOWN) {
    return {
      format: RenderFormat.MARKDOWN,
    }
  }

  return null
}

function withPdfExtension(fileName: string) {
  return fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`
}

export function buildAgentReferenceViewerSession(args: BuildAgentReferenceViewerSessionArgs): ContentViewerSession | null {
  const { reference, matterId, citationHighlightIndex, defaultTab, userFile } = args
  const highlights = getHighlightsForCitation(reference, citationHighlightIndex)
  const context = buildChatCitationContext(reference, citationHighlightIndex)

  if (reference.type === AgentReferenceType.CASELAW) {
    const title = agentReferenceMetadataToTitle(reference)
    const downloadFileName = reference.metadata.title ?? 'source'
    const storagePath = reference.metadata.s3_path

    return createViewerSession({
      document: {
        kind: ViewerDocumentKind.CASELAW,
        s3Dir: reference.metadata.s3_dir,
        s3Path: storagePath,
        parentId: reference.metadata.parent_id,
        caseStatus: reference.metadata.case_status,
        title,
        renderAsset: {
          format: RenderFormat.HTML,
          sourceKind: ViewerDocumentKind.CASELAW,
          storagePath,
        },
        downloadTarget: {
          kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX,
          sourceKind: ViewerDocumentKind.CASELAW,
          storagePath,
          fileName: downloadFileName,
        },
      },
      context,
      presentation: {
        markdownHighlights: highlights.sentences,
        ...(defaultTab ? { defaultTab } : {}),
      },
    })
  }

  if (reference.type === AgentReferenceType.LRR) {
    const title = agentReferenceMetadataToTitle(reference)
    const downloadFileName = reference.metadata.name ?? 'source'
    const storagePath = reference.metadata.s3_path

    return createViewerSession({
      document: {
        kind: ViewerDocumentKind.LRR,
        s3Path: storagePath,
        title,
        renderAsset: {
          format: RenderFormat.HTML,
          sourceKind: ViewerDocumentKind.LRR,
          storagePath,
        },
        downloadTarget: {
          kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX,
          sourceKind: ViewerDocumentKind.LRR,
          storagePath,
          fileName: downloadFileName,
        },
      },
      context,
      presentation: {
        markdownHighlights: highlights.sentences,
      },
    })
  }

  if (reference.type === AgentReferenceType.TRELLIS_WRITING_SAMPLE) {
    const title = agentReferenceMetadataToTitle(reference)

    return createViewerSession({
      document: {
        kind: ViewerDocumentKind.WRITING_SAMPLE,
        referenceId: reference.reference.id,
        title,
        ...(matterId
          ? {
              downloadTarget: {
                kind: ViewerDownloadTargetKind.WRITING_SAMPLE_PDF,
                referenceId: reference.reference.id,
                matterId,
                fileName: withPdfExtension(title),
              },
            }
          : {}),
      },
      context,
    })
  }

  const target = resolveFileReferenceTarget({
    reference,
    matterId: matterId ?? null,
  })

  if (target.kind === FileReferenceTargetKind.CANONICAL) {
    const viewableContent = target.fileReference?.viewable_content ?? userFile?.viewable_content ?? null
    if (!target.userFileId || !viewableContent) return null

    const renderAsset = buildUserFileReferenceRenderAsset(viewableContent)
    if (!renderAsset) return null

    return createViewerSession({
      document: {
        kind: ViewerDocumentKind.USER_FILE,
        userFileId: target.userFileId,
        title: agentReferenceMetadataToTitle(reference),
        renderAsset,
        downloadTarget: {
          kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL,
          userFileId: target.userFileId,
        },
      },
      context,
      presentation: {
        pdfHighlights: highlights.areas,
        markdownHighlights: highlights.sentences,
        startingPage: target.startingPage,
      },
    })
  }

  if (target.kind === FileReferenceTargetKind.LEGACY) {
    const format = target.filePath.toLowerCase().endsWith('.pdf') ? RenderFormat.PDF : RenderFormat.MARKDOWN
    const storageConfig = resolveReferenceStorage({
      path: target.filePath,
      format: format === RenderFormat.PDF ? 'pdf' : 'markdown',
      metadata: reference.metadata,
    })
    const resolvedPath = storageConfig.legacy_always_cloud ? target.filePath : storageConfig.normalizedPath || target.filePath

    return createViewerSession({
      document: {
        kind: ViewerDocumentKind.LEGACY_FILE,
        id: target.filePath,
        title: agentReferenceMetadataToTitle(reference),
        renderAsset: {
          format,
          filePath: resolvedPath,
          gcsBucketRef: storageConfig.bucketRef,
          legacyAlwaysCloud: storageConfig.legacy_always_cloud,
        },
        downloadTarget: {
          kind: ViewerDownloadTargetKind.LEGACY_OWNER_FILE,
          filePath: target.filePath,
        },
      },
      context,
      presentation: {
        pdfHighlights: highlights.areas,
        markdownHighlights: highlights.sentences,
        startingPage: target.startingPage,
      },
    })
  }

  return null
}
